/**
 * Audio system — single source of truth for music + SFX playback.
 *
 * Phase 15a. Sits above Phaser's sound manager so callers don't reach into
 * scene.sound directly. Two buses (music, sfx) with independent volume +
 * a master mute. Music crossfades between tracks; SFX is fire-and-forget.
 *
 * Asset pipeline: packages/audio-bake/ renders the pokefirered MIDIs
 * through fluidsynth + a community GBA SoundFont and emits OGGs into
 * apps/game/public/assets/audio/{music,sfx}/. AudioSystem loads them on
 * demand (music lazy, SFX preloaded by WorldScene).
 *
 * Graceful-missing semantics: when an asset fails to load (404 — the bake
 * hasn't run yet, or a key is missing), playMusic/playSfx no-op silently
 * after one console.warn. The game stays playable; audio just doesn't fire.
 *
 * iOS Safari requires a user gesture before AudioContext starts. The Title
 * scene calls unlock() on its first interactable input. After unlock() the
 * gate flips permanently for the session.
 *
 * Volume + mute are persisted to localStorage under a key separate from
 * the run save snapshot — audio prefs survive Reset Run.
 */
import Phaser from 'phaser';

const PREFS_KEY = 'poke-audio';
const DEFAULT_MUSIC_VOLUME = 0.5;
const DEFAULT_SFX_VOLUME = 0.7;

/** Music keys we expect bake-audio to produce. Adding a key here is the
 *  contract — the bake script reads the same list and emits matching OGGs. */
export const MUSIC_KEYS = [
  'title',
  'pallet',
  'route1',
  'pewter',
  'viridian_forest',
  'poke_center',
  'gym',
  'wild_battle',
  'trainer_battle',
  'gym_leader_battle',
  'victory_wild',
  'victory_trainer',
  'victory_gym',
] as const;
export type MusicKey = (typeof MUSIC_KEYS)[number];

/** SFX keys. Preloaded eagerly (each ≤8KB OGG). Missing assets no-op
 *  silently via the FILE_LOAD_ERROR + `missing` set wiring — keys that
 *  don't yet have an OGG (egg_pull pending audio-bake; tracked in the
 *  Pokédex implementation handoff) just stay quiet without crashing. */
export const SFX_KEYS = [
  'select',          // menu / dialogue advance
  'menu_open',
  'faint',
  'low_health',      // optional ambient when active mon is < 25% HP
  'exp_gain',
  'item_get',
  'alert',
  'level_up',        // short stinger; can play on top of music
  'hit_normal',
  'hit_super',       // super-effective hit
  'heal',
  'encounter',
  'ball_open',       // draft pick confirmation
  'egg_pull',        // rare-pull reveal on a draft card (Step 4 — asset
                     // bake is a follow-up; the SFX_KEYS slot exists so
                     // the playSfx call site has a stable enum value)
] as const;
export type SfxKey = (typeof SFX_KEYS)[number];

interface AudioPrefs {
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
}

function loadPrefs(): AudioPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { musicVolume: DEFAULT_MUSIC_VOLUME, sfxVolume: DEFAULT_SFX_VOLUME, muted: false };
    const parsed = JSON.parse(raw) as Partial<AudioPrefs>;
    return {
      musicVolume: typeof parsed.musicVolume === 'number' ? clamp01(parsed.musicVolume) : DEFAULT_MUSIC_VOLUME,
      sfxVolume: typeof parsed.sfxVolume === 'number' ? clamp01(parsed.sfxVolume) : DEFAULT_SFX_VOLUME,
      muted: !!parsed.muted,
    };
  } catch {
    return { musicVolume: DEFAULT_MUSIC_VOLUME, sfxVolume: DEFAULT_SFX_VOLUME, muted: false };
  }
}

function savePrefs(prefs: AudioPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Quota / private browsing — don't break the game over a prefs flush
  }
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

class AudioSystem {
  private game: Phaser.Game | null = null;
  private prefs: AudioPrefs = loadPrefs();
  /** Keys we've already attempted to load and failed; future plays no-op. */
  private missing = new Set<string>();
  /** Active music track (Phaser sound). One at a time; crossfade transitions
   *  through a temporary fade-out of the old track. */
  private currentMusic: Phaser.Sound.BaseSound | null = null;
  private currentMusicKey: MusicKey | null = null;
  /** Tween active for the currentMusic's volume. Tracked so a new playMusic
   *  can cancel an in-flight fade-in cleanly. */
  private musicTween: Phaser.Tweens.Tween | null = null;
  private unlocked = false;

  /** Bind to the running Phaser.Game once at boot. Must be called before
   *  any play* method.
   *
   *  Applies the persisted mute state to Phaser's master mute node here
   *  so it's in effect BEFORE any playMusic call. Without this, the
   *  per-sound `{ volume: 0 }` config used to race the volume-fade-in
   *  tween: the tween's first update reads `gain.value` via the
   *  WebAudioSound `volume` getter, which can return the GainNode's
   *  default 1 if the `setValueAtTime(0, 0)` schedule from `applyConfig`
   *  hasn't yet been observed by the audio engine. The tween then
   *  animates 1 → musicVolume over fadeMs, producing an audible leak
   *  for the full fade duration despite our intent of 0 → 0. Master
   *  mute sits at the output stage and zeros ALL audio regardless of
   *  per-sound volume, sidestepping the race entirely. */
  attach(game: Phaser.Game): void {
    this.game = game;
    if (this.prefs.muted) {
      game.sound.setMute(true);
    }
  }

  /** iOS Safari + some Android browsers require a user-gesture-initiated
   *  AudioContext.resume() before audio can start. Wired to TitleScene's
   *  first interactable input. Idempotent. */
  unlock(): void {
    if (this.unlocked || !this.game) return;
    const sm = this.game.sound as Phaser.Sound.WebAudioSoundManager;
    if (sm.context && sm.context.state === 'suspended') {
      void sm.context.resume();
    }
    this.unlocked = true;
  }

  // ── Preload helpers ────────────────────────────────────────

  /** Eagerly preload all SFX into the given scene's loader. Call from a
   *  scene's preload(). Loader handles missing files via FILE_LOAD_ERROR
   *  events; we listen and add the key to `missing` so playSfx no-ops. */
  preloadAllSfx(loader: Phaser.Loader.LoaderPlugin): void {
    for (const key of SFX_KEYS) {
      if (this.missing.has(`sfx:${key}`)) continue;
      loader.audio(`sfx:${key}`, `assets/audio/sfx/${key}.ogg`);
    }
    loader.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      if (file.key.startsWith('sfx:') || file.key.startsWith('music:')) {
        this.missing.add(file.key);
      }
    });
  }

  /** Lazy-load a music track. Returns a promise that resolves when ready
   *  (or rejects → caller treats as missing). */
  private loadMusic(scene: Phaser.Scene, key: MusicKey): Promise<void> {
    const cacheKey = `music:${key}`;
    if (scene.cache.audio.has(cacheKey)) return Promise.resolve();
    if (this.missing.has(cacheKey)) return Promise.reject(new Error('missing'));
    return new Promise((resolve, reject) => {
      const onComplete = (): void => resolve();
      const onError = (file: Phaser.Loader.File): void => {
        if (file.key === cacheKey) {
          this.missing.add(cacheKey);
          reject(new Error('load failed'));
        }
      };
      scene.load.once(Phaser.Loader.Events.COMPLETE, onComplete);
      scene.load.once(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      scene.load.audio(cacheKey, `assets/audio/music/${key}.ogg`);
      scene.load.start();
    });
  }

  // ── Music ───────────────────────────────────────────────────

  /** Switch to a music track, crossfading from any currently playing one.
   *  No-ops if the key matches the currently playing track. */
  async playMusic(scene: Phaser.Scene, key: MusicKey, opts: { fadeMs?: number } = {}): Promise<void> {
    if (!this.game) return;
    if (this.currentMusicKey === key && this.currentMusic?.isPlaying) return;
    const fadeMs = opts.fadeMs ?? 800;

    // Fade out + stop the existing track first. We don't await — the new
    // track can start ramping in immediately (true crossfade).
    if (this.currentMusic) {
      const oldSound = this.currentMusic;
      this.musicTween?.stop();
      scene.tweens.add({
        targets: oldSound,
        volume: 0,
        duration: fadeMs,
        onComplete: () => {
          oldSound.stop();
          oldSound.destroy();
        },
      });
      this.currentMusic = null;
      this.currentMusicKey = null;
    }

    try {
      await this.loadMusic(scene, key);
    } catch {
      return; // missing — silent
    }

    // Always tween toward the configured musicVolume — silencing when
    // muted is handled by Phaser's master mute node (see attach()), NOT
    // by zeroing this per-sound target. The prior `muted ? 0 : musicVolume`
    // ternary made the tween 0 → 0 in theory, but on a cold gain node
    // the tween's first read of `sound.volume` could pick up the default
    // 1 (before `applyConfig`'s scheduled change was observed), producing
    // an audible 1 → 0 fade for the full fadeMs.
    const sound = scene.sound.add(`music:${key}`, { loop: true, volume: 0 });
    sound.play();
    this.currentMusic = sound;
    this.currentMusicKey = key;
    this.musicTween = scene.tweens.add({
      targets: sound,
      volume: this.prefs.musicVolume,
      duration: fadeMs,
    });
  }

  /** Fade out + stop the current track. */
  stopMusic(scene: Phaser.Scene, opts: { fadeMs?: number } = {}): void {
    if (!this.currentMusic) return;
    const fadeMs = opts.fadeMs ?? 600;
    const old = this.currentMusic;
    this.musicTween?.stop();
    scene.tweens.add({
      targets: old,
      volume: 0,
      duration: fadeMs,
      onComplete: () => {
        old.stop();
        old.destroy();
      },
    });
    this.currentMusic = null;
    this.currentMusicKey = null;
  }

  // ── SFX ─────────────────────────────────────────────────────

  /** Fire a one-shot SFX. No-op if the key isn't loaded (preload didn't
   *  run, or the asset is missing). */
  playSfx(key: SfxKey, opts: { volume?: number } = {}): void {
    if (!this.game || this.muted) return;
    const cacheKey = `sfx:${key}`;
    if (this.missing.has(cacheKey)) return;
    if (!this.game.cache.audio.has(cacheKey)) return;
    const volume = (opts.volume ?? 1) * this.prefs.sfxVolume;
    this.game.sound.play(cacheKey, { volume });
  }

  // ── Prefs ───────────────────────────────────────────────────

  get musicVolume(): number {
    return this.prefs.musicVolume;
  }
  get sfxVolume(): number {
    return this.prefs.sfxVolume;
  }
  get muted(): boolean {
    return this.prefs.muted;
  }

  setMusicVolume(v: number): void {
    this.prefs.musicVolume = clamp01(v);
    savePrefs(this.prefs);
    // No `!this.muted` gate — master mute zeros output. Keeping the
    // per-sound volume in sync with the pref means toggling mute off
    // restores the right level without a separate sync step.
    if (this.currentMusic) {
      (this.currentMusic as Phaser.Sound.WebAudioSound).setVolume(this.prefs.musicVolume);
    }
  }

  setSfxVolume(v: number): void {
    this.prefs.sfxVolume = clamp01(v);
    savePrefs(this.prefs);
  }

  setMuted(muted: boolean): void {
    this.prefs.muted = muted;
    savePrefs(this.prefs);
    // Master mute on the SoundManager is instant (single GainNode at the
    // output stage) and silences EVERYTHING — music, in-flight tweens,
    // SFX, lock-screen ghosts. Per-sound volume gymnastics raced with
    // the `setValueAtTime(0, 0)` schedule and leaked audio for fadeMs.
    if (this.game) {
      this.game.sound.setMute(muted);
    }
  }

  /** True iff the audio bake has produced at least one music asset. Used
   *  by Settings UI to decide whether to render the sliders or a
   *  placeholder ("audio assets not built — run pnpm bake-audio"). */
  hasAnyMusicLoaded(): boolean {
    if (!this.game) return false;
    for (const key of MUSIC_KEYS) {
      if (this.game.cache.audio.has(`music:${key}`)) return true;
    }
    return false;
  }

  /** True when the most recent state transition paused audio. Used to
   *  gate resume() so we don't accidentally un-pause on iOS lock-screen
   *  glances (which fire visibilitychange=visible without the page
   *  actually being on screen). */
  private wasAutoPaused = false;

  /** Pause every active sound + STOP the music source + suspend the
   *  audio context. Wired to a wide net of "page is not visible"
   *  signals in main.ts (visibilitychange hidden, blur, pagehide,
   *  freeze) — iOS Safari fires different events depending on whether
   *  you sleep the device, switch tabs, or background the app, so
   *  catching any one of them keeps audio honest.
   *
   *  We FULLY STOP the music source rather than pause it because iOS
   *  Safari has a known bug where suspended-but-not-stopped Web Audio
   *  sources can keep ringing in the lock-screen "Now Playing" cache
   *  for several seconds. resumeAll() restarts the same key from
   *  position 0, which loses sub-second progress but eliminates the
   *  ghost-audio class of bugs. */
  pauseAll(): void {
    if (!this.game) return;
    // Stop the active music source outright. We keep currentMusicKey
    // around so resumeAll can restart the same track.
    if (this.currentMusic) {
      const old = this.currentMusic;
      this.musicTween?.stop();
      this.musicTween = null;
      try {
        old.stop();
        old.destroy();
      } catch {
        // already destroyed; ignore
      }
      this.currentMusic = null;
      // Intentionally keep currentMusicKey set so resumeAll can re-fire.
    }
    this.game.sound.pauseAll();
    const ctx = (this.game.sound as Phaser.Sound.WebAudioSoundManager).context;
    if (ctx && ctx.state === 'running') {
      void ctx.suspend();
    }
    this.wasAutoPaused = true;
  }

  /** Resume audio — but ONLY when the page is actually in the
   *  foreground rendering. iOS Safari fires visibilitychange=visible
   *  on lock-screen-view (power-button tap while locked), and a naive
   *  resume blasts music out the device speakers under the lock UI.
   *
   *  We gate resume on a double requestAnimationFrame: rAF only ticks
   *  when the page is truly visible and rendering, so if iOS calls
   *  visible on a lock-screen glance the inner callback never runs and
   *  audio stays silent. When the user actually unlocks + returns,
   *  rAF resumes ticking and music comes back.
   *
   *  Idempotent — only triggers if a prior pauseAll set wasAutoPaused. */
  resumeAll(scene?: Phaser.Scene): void {
    if (!this.game || !this.wasAutoPaused) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.game || !this.wasAutoPaused) return;
        this.wasAutoPaused = false;
        const ctx = (this.game.sound as Phaser.Sound.WebAudioSoundManager).context;
        if (ctx && ctx.state === 'suspended') {
          void ctx.resume();
        }
        this.game.sound.resumeAll();
        // Restart the music track we stopped. Use the active scene if
        // one was passed (caller has scene context); otherwise pick the
        // first running scene so playMusic has a tween manager + loader.
        if (this.currentMusicKey) {
          const targetScene = scene ?? this.firstActiveScene();
          if (targetScene) {
            const key = this.currentMusicKey;
            this.currentMusicKey = null; // playMusic skips if same key — clear so it re-fires
            void this.playMusic(targetScene, key, { fadeMs: 400 });
          }
        }
      });
    });
  }

  private firstActiveScene(): Phaser.Scene | null {
    if (!this.game) return null;
    const scenes = this.game.scene.getScenes(true);
    return scenes[0] ?? null;
  }
}

export const audio = new AudioSystem();
